import React, { useState, useEffect } from "react";
import { AgGrid, Button, Flex, Text } from "@jsq/ds/core";
import { JIRA_BASE_URL } from "../../../../constants";
import {
  bulkActionsPanel,
  bulkActionsPanelHeader,
  actionsSection,
  divider,
} from "./styles_vanilla.css";
import type { WorkflowTableRow } from "../../../types";

type WorkflowBulkActionsPanelProps = {
  api: AgGrid.GridApi<WorkflowTableRow>;
};

function openWorkflowRowsInJira(selectedRows: WorkflowTableRow[]): void {
  const ticketKeys = selectedRows
    .filter((row) => row.isParentRow && row.externalRefId)
    .map((row) => row.externalRefId as string);

  if (ticketKeys.length === 0) return;

  const jiraProjectKey = ticketKeys[0].split("-")[0];
  const jqlQuery = `issue IN (${ticketKeys.join(", ")})`;
  const encodedJql = encodeURIComponent(jqlQuery);
  const jiraUrl = `${JIRA_BASE_URL}/jira/software/c/projects/${jiraProjectKey}/issues/?jql=${encodedJql}`;

  window.open(jiraUrl, "_blank");
}

export const WorkflowBulkActionsPanel = ({ api }: WorkflowBulkActionsPanelProps) => {
  const [selectedRows, setSelectedRows] = useState<WorkflowTableRow[]>([]);

  useEffect(() => {
    const onSelectionChanged = () => {
      setSelectedRows(api.getSelectedRows());
    };

    api.addEventListener("selectionChanged", onSelectionChanged);

    return () => {
      api.removeEventListener("selectionChanged", onSelectionChanged);
    };
  }, [api]);

  const clearSelection = () => {
    api.deselectAll();
  };

  const handleOpenInJira = () => {
    openWorkflowRowsInJira(selectedRows);
  };

  const isOpenInJiraDisabled = !selectedRows.some(
    (row) => row.isParentRow && row.externalRefId,
  );

  return (
    <Flex
      flexDirection="column"
      className={bulkActionsPanel}
      data-testid="workflow-bulk-actions-panel"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        className={bulkActionsPanelHeader}
      >
        <Text variant="regular-md" data-testid="selected-count">
          {selectedRows.length} selected
        </Text>
        {selectedRows.length > 0 && (
          <Button variant="link" onClick={clearSelection} data-testid="clear-selection">
            Clear selection
          </Button>
        )}
      </Flex>
      <div className={divider} />
      <Flex flexDirection="column" className={actionsSection}>
        <Text variant="secondary-md" marginBottom="md">
          JIRA
        </Text>
        <Button
          variant="neutral"
          onClick={handleOpenInJira}
          data-testid="open-in-jira"
          disabled={isOpenInJiraDisabled}
        >
          Open tickets in Jira
        </Button>
      </Flex>
      <div className={divider} />
    </Flex>
  );
};
